import {attr, controller, target} from '@github/catalyst'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

@controller('ghas-calculator')
export class GhasCalculatorElement extends HTMLElement {
  @target declare committerCountInput: HTMLInputElement
  @target declare codeSecurityCommitterCountInput: HTMLInputElement
  @target declare secretProtectionCommitterCountInput: HTMLInputElement

  get committerCount() {
    return parseInt(this.committerCountInput.value) || 0
  }

  get codeSecurityCommitterCount() {
    return parseInt(this.codeSecurityCommitterCountInput.value) || 0
  }

  get secretProtectionCommitterCount() {
    return parseInt(this.secretProtectionCommitterCountInput.value) || 0
  }

  @target declare committerSeatCostDisplay: HTMLElement
  @target declare costPerMonthDisplay: HTMLElement

  @attr costPerMonth = 0
  @attr committerSeatCost = 0
  @attr active = false

  declare unit_data: UnitData

  connectedCallback() {
    this.unit_data = getUnitData()
  }

  updateCommitterSeatCost() {
    const codeSecurityCost = this.calculateCommitterSeatCost(
      this.codeSecurityCommitterCount,
      this.unit_data.ghas_code_security!.value,
    )
    const secretProtectionCost = this.calculateCommitterSeatCost(
      this.secretProtectionCommitterCount,
      this.unit_data.ghas_secret_protection!.value,
    )
    this.committerSeatCost = codeSecurityCost + secretProtectionCost
  }

  calculateCommitterSeatCost(committerCount: number, perSeatCost: number) {
    return perSeatCost * committerCount
  }

  calculateSelfHostedCost() {
    return 0
  }

  updateMonthlyCost() {
    this.costPerMonth = this.committerSeatCost
  }

  displayCost(displayElement: HTMLElement, price: number) {
    displayElement.textContent = currencyFormatter.format(price / 100)
  }

  resetButtonClicked(event: PointerEvent) {
    event.preventDefault()
    if (this.codeSecurityCommitterCountInput) this.codeSecurityCommitterCountInput.value = ''
    if (this.secretProtectionCommitterCountInput) this.secretProtectionCommitterCountInput.value = ''
    this.committerSeatCost = 0
    this.costPerMonth = 0
  }

  getSummaryForCSV() {
    // headers: ['Service', 'Configuration Summary', 'Notes', 'Cost Per Month']

    const codeSecurityCost = this.unit_data?.ghas_code_security?.value || 0
    const secretProtectionCost = this.unit_data?.ghas_secret_protection?.value || 0

    return [
      [
        'GitHub Advanced Security',
        `"Code Security Committers: ${this.codeSecurityCommitterCount}, Secret Protection Committers: ${this.secretProtectionCommitterCount}"`,
        `"Code Security Cost Per Month: ${currencyFormatter.format(
          (codeSecurityCost * this.codeSecurityCommitterCount) / 100,
        )}, Secret Protection Cost Per Month: ${currencyFormatter.format(
          (secretProtectionCost * this.secretProtectionCommitterCount) / 100,
        )}"`,
        `"${currencyFormatter.format(this.costPerMonth / 100)}"`,
      ],
    ]
  }

  static observedAttributes = ['data-committer-seat-cost', 'data-cost-per-month', 'data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return
    }

    if (name === 'data-committer-seat-cost') {
      this.displayCost(this.committerSeatCostDisplay, this.committerSeatCost)
      this.updateMonthlyCost()
    }

    if (name === 'data-cost-per-month' || name === 'data-active') {
      this.displayCost(this.costPerMonthDisplay, this.costPerMonth)
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'ghasTotal', amount: this.costPerMonth, active: this.active},
        }),
      )
    }

    if (name === 'data-cost-per-month') {
      document.dispatchEvent(
        new CustomEvent('formDataUpdated', {
          detail: {
            sectionName: 'ghas',
            resetEnabled: Boolean(this.costPerMonth),
          },
        }),
      )
    }
  }
}
